import React, { useEffect, useRef, useState } from 'react';

import { FolderOpenOutlined, TranslationOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Tooltip,
  message,
} from 'antd';
import type { InputRef } from 'antd';
import { useTranslation } from 'react-i18next';

import { icons } from 'components/DynamicIcon/DynamicIcon';

import { MenuNode } from 'models/MenuNode';

import {
  CreateMenuForCurrentCompanyRequestDto,
  MenuService,
} from 'services/MenuService';

import { MenuType } from '../../../../config/Config';

interface NewContainerModalProps {
  parentNode: MenuNode | null;
  isOpen: boolean;
  handleModalCancel: () => void;
  handleNewMenuCreated: (newCreatedNode: MenuNode) => void;
}

function NewContainerModal({
  parentNode,
  isOpen,
  handleModalCancel,
  handleNewMenuCreated,
}: NewContainerModalProps) {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [useLocalizedTitles, setUseLocalizedTitles] = useState(false);
  const titleInputRef = useRef<InputRef>(null);

  const formattedIcons = Object.entries(icons)
    .map(([key, value]) => ({
      value: key,
      label: key,
      icon: value,
    }))
    .map((item) => ({
      value: item.value,
      label: (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          {item.icon && <span style={{ marginRight: 8 }}>{item.icon}</span>}
          {item.label}
        </div>
      ),
    }));

  useEffect(() => {
    if (isOpen) {
      form.resetFields();
      setUseLocalizedTitles(false);
      // Enfocar y seleccionar el input de título al abrir
      setTimeout(() => {
        titleInputRef.current?.focus();
        // Seleccionar todo el contenido
        titleInputRef.current?.select?.();
      }, 0);
    }
  }, [isOpen, parentNode]);

  const onFinish = async (values: any) => {
    setLoading(true);

    try {
      const body: CreateMenuForCurrentCompanyRequestDto = {
        title: values.title.trim(),
        localizedTitles: useLocalizedTitles
          ? {
              es: (values.localizedTitleEs || '').trim() || null,
              en: (values.localizedTitleEn || '').trim() || null,
            }
          : null,
        menuType: MenuType.PARENT_MENU,
        redirectLink: null,
        icon:
          values.icon === undefined || values.icon === null
            ? null
            : (values.icon.value as string) || null,
        parentMenuCode: parentNode?.code || null,
        additionalConfig: {},
      };

      const response = await MenuService.createForCurrentCompany(body);

      if (response.success) {
        const { newMenuNode } = response.data as { newMenuNode: MenuNode };
        message.success(
          t(
            'scenes.new_report_and_menus.components.new_container_modal.success.created'
          ),
          3.5
        );
        handleNewMenuCreated(newMenuNode);
        handleModalCancel();
      } else {
        const err = response.data as any;
        message.error(
          err?.message ||
            t(
              'scenes.new_report_and_menus.components.new_container_modal.errors.create_failed'
            ),
          3.5
        );
      }
    } catch (error: any) {
      message.error(
        t(
          'scenes.new_report_and_menus.components.new_container_modal.errors.unexpected_error_create'
        ),
        3.5
      );
    } finally {
      setLoading(false);
    }
  };
  return (
    <Modal
      title={t(
        'scenes.new_report_and_menus.components.new_container_modal.title'
      )}
      open={isOpen}
      onCancel={handleModalCancel}
      maskClosable={false}
      destroyOnClose
      footer={null}
      width={480}
    >
      <Divider style={{ marginTop: '15px', marginBottom: '15px' }} />

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24} style={{ marginBottom: '18px' }}>
          <Col xs={24}>
            <Alert
              message={t(
                'scenes.new_report_and_menus.components.new_container_modal.alert_added_to_parent'
              )}
              type="info"
              showIcon
            />
          </Col>
        </Row>

        {/* Contenedor padre (similar a NewReportModal) */}
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.parent_container'
              )}
            >
              <Input
                addonBefore={<FolderOpenOutlined />}
                type="text"
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_container_modal.placeholders.parent_container'
                )}
                readOnly
                defaultValue={
                  parentNode ? parentNode.title : `${window.location.origin}`
                }
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="title"
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.title'
              )}
              extra={t(
                'scenes.new_report_and_menus.components.new_container_modal.hints.title_fallback'
              )}
              rules={[
                {
                  required: true,
                  message: t(
                    'scenes.new_report_and_menus.components.new_container_modal.validations.title.required'
                  ),
                },
              ]}
            >
              <Input
                ref={titleInputRef}
                type="text"
                disabled={loading}
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_container_modal.placeholders.title'
                )}
                maxLength={80}
                suffix={
                  <Tooltip
                    title={t(
                      'scenes.new_report_and_menus.components.new_container_modal.tooltips.add_translations'
                    )}
                  >
                    <TranslationOutlined
                      onClick={() => {
                        const next = !useLocalizedTitles;
                        setUseLocalizedTitles(next);
                        if (next) {
                          const base = (
                            form.getFieldValue('title') || ''
                          ).trim();
                          form.setFieldsValue({
                            localizedTitleEs: base,
                            localizedTitleEn: base,
                          });
                        } else {
                          form.setFieldsValue({
                            localizedTitleEs: '',
                            localizedTitleEn: '',
                          });
                        }
                      }}
                      style={{
                        cursor: 'pointer',
                        color: useLocalizedTitles ? '#1677ff' : undefined,
                      }}
                    />
                  </Tooltip>
                }
              />
            </Form.Item>
          </Col>
        </Row>

        {useLocalizedTitles && (
          <div
            style={{
              border: '1px solid #f0f0f0',
              borderRadius: 8,
              padding: 12,
              marginBottom: 12,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
              }}
            >
              <TranslationOutlined />
              <span>
                {t(
                  'scenes.new_report_and_menus.components.new_container_modal.sections.translations'
                )}
              </span>
            </div>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="localizedTitleEn"
                  label={t(
                    'scenes.new_report_and_menus.components.new_container_modal.labels.title_en'
                  )}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_container_modal.placeholders.title_en'
                    )}
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={12}>
              <Col xs={24}>
                <Form.Item
                  name="localizedTitleEs"
                  label={t(
                    'scenes.new_report_and_menus.components.new_container_modal.labels.title_es'
                  )}
                >
                  <Input
                    type="text"
                    disabled={loading}
                    placeholder={t(
                      'scenes.new_report_and_menus.components.new_container_modal.placeholders.title_es'
                    )}
                    maxLength={80}
                  />
                </Form.Item>
              </Col>
            </Row>
          </div>
        )}

        {/* Input de icono: opcional y siempre visible */}
        <Row gutter={24}>
          <Col xs={24} lg={24}>
            <Form.Item
              name="icon"
              label={t(
                'scenes.new_report_and_menus.components.new_container_modal.labels.icon'
              )}
            >
              <Select
                placeholder={t(
                  'scenes.new_report_and_menus.components.new_container_modal.placeholders.select_option'
                )}
                allowClear
                labelInValue
                options={formattedIcons}
                showSearch
                disabled={loading}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={24}>
          <Col xs={24}>
            <Button
              onClick={() => {
                form.submit();
              }}
              loading={loading}
              type="primary"
              block
            >
              {t(
                'scenes.new_report_and_menus.components.new_container_modal.buttons.save_changes'
              )}
            </Button>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

export default NewContainerModal;
